// Public station application page (RCS-41).
//
// The only unauthenticated form in the product. It posts to
// /api/onboarding/applications, which stores a row and does nothing else --
// no station, no config, no account. Everything on this page is deliberately
// free of the platform: no Auth0, no tenant config, no session.

import React, { useState } from 'react';
import useTitle from './useTitle';
import './onboarding.css';

const SERVICES = [
  ['streaming', 'Streaming and player'],
  ['website', 'Website'],
  ['podcasting', 'Podcasting'],
  ['monetization', 'Monetization and ads'],
  ['merchandise', 'Merchandise store'],
  ['live-video', 'Live video streaming'],
];

const CONTENT_TYPES = [
  ['', 'Choose one'],
  ['music', 'Music radio'],
  ['talk', 'Talk'],
  ['podcast', 'Podcast'],
  ['mixed', 'A mix'],
  ['other', 'Something else'],
];

const AUDIENCE = [
  ['', 'Choose one'],
  ['under-1k', 'Under 1,000 a month'],
  ['1k-10k', '1,000 to 10,000 a month'],
  ['10k-50k', '10,000 to 50,000 a month'],
  ['50k-plus', 'More than 50,000 a month'],
  ['just-starting', 'Just starting out'],
];

const EMPTY = {
  contactName: '',
  contactEmail: '',
  contactPhone: '',
  stationName: '',
  contentType: '',
  audienceSize: '',
  streamUrl: '',
  links: '',
  applicantNote: '',
  website_confirm: '', // honeypot
};

export default function ApplyPage() {
  useTitle('Apply — Creator Rabbit');
  const [form, setForm] = useState(EMPTY);
  const [services, setServices] = useState(() => new Set(['streaming']));
  const [state, setState] = useState('editing'); // editing | sending | sent
  const [error, setError] = useState(null);
  const [badFields, setBadFields] = useState([]);

  const set = (name) => (e) => setForm((f) => ({ ...f, [name]: e.target.value }));

  const toggleService = (key) =>
    setServices((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const submit = async (e) => {
    e.preventDefault();
    setState('sending');
    setError(null);
    setBadFields([]);

    try {
      const res = await fetch('/api/onboarding/applications', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, serviceInterests: Array.from(services) }),
      });
      const body = await res.json().catch(() => ({}));

      if (!res.ok) {
        setBadFields(body.fields || []);
        // The API's messages are written to be read by an applicant, so they
        // are shown as-is rather than mapped to something vaguer.
        setError(body.error_description || 'Something went wrong. Please try again.');
        setState('editing');
        return;
      }
      setState('sent');
      window.scrollTo(0, 0);
    } catch (err) {
      setError('We could not reach the server. Please check your connection and try again.');
      setState('editing');
    }
  };

  if (state === 'sent') {
    return (
      <div className="ob-page">
        <Bar />
        <div className="ob-wrap">
          <div className="ob-center">
            <div className="ob-tick" aria-hidden="true">&#10003;</div>
            <h1>Thanks &mdash; we have your application</h1>
            <p>
              Someone at Creator Rabbit will read it and come back to you, usually within a
              couple of working days. If it looks like a fit we will talk through what you
              need, and then send you an invitation to set your station up.
            </p>
            <p>
              Nothing has been charged and nothing is live yet. You do not need to do anything
              else for now.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const invalid = (name) => (badFields.includes(name) ? 'true' : undefined);

  return (
    <div className="ob-page">
      <Bar />
      <div className="ob-wrap">
        <div className="ob-grid">
          <div className="ob-lede">
            <h1>Tell us what you want to build</h1>
            <p>
              We work with stations, creators and podcasters to put a real broadcast
              operation behind their audience. Tell us about yours and we will come back
              to you.
            </p>
            <ol className="ob-steps">
              <li>
                <span className="n">1</span>
                <p>You send this form.</p>
              </li>
              <li>
                <span className="n">2</span>
                <p>We read it and talk to you about what you need and what it costs.</p>
              </li>
              <li>
                <span className="n">3</span>
                <p>If it is a fit, we send you an invitation and you set your station up yourself.</p>
              </li>
            </ol>
          </div>

          <form className="ob-card" onSubmit={submit} noValidate>
            <h2>Apply</h2>
            <p className="sub">Takes about three minutes. No account, no password, no card.</p>

            {error && (
              <div className="ob-note bad" role="alert">
                <span>{error}</span>
              </div>
            )}

            <div className="ob-row">
              <div className="ob-field">
                <label htmlFor="ob-name">Your name</label>
                <input
                  id="ob-name"
                  value={form.contactName}
                  onChange={set('contactName')}
                  aria-invalid={invalid('contactName')}
                  autoComplete="name"
                />
              </div>
              <div className="ob-field">
                <label htmlFor="ob-email">Email</label>
                <input
                  id="ob-email"
                  type="email"
                  value={form.contactEmail}
                  onChange={set('contactEmail')}
                  aria-invalid={invalid('contactEmail')}
                  autoComplete="email"
                />
              </div>
            </div>

            <div className="ob-row">
              <div className="ob-field">
                <label htmlFor="ob-phone">
                  Phone <span className="opt">Optional</span>
                </label>
                <input id="ob-phone" value={form.contactPhone} onChange={set('contactPhone')} autoComplete="tel" />
              </div>
              <div className="ob-field">
                <label htmlFor="ob-station">Station or brand name</label>
                <input
                  id="ob-station"
                  value={form.stationName}
                  onChange={set('stationName')}
                  aria-invalid={invalid('stationName')}
                />
              </div>
            </div>

            <div className="ob-row">
              <div className="ob-field">
                <label htmlFor="ob-content">What do you make?</label>
                <select id="ob-content" value={form.contentType} onChange={set('contentType')}>
                  {CONTENT_TYPES.map(([v, label]) => (
                    <option key={v} value={v}>{label}</option>
                  ))}
                </select>
              </div>
              <div className="ob-field">
                <label htmlFor="ob-audience">Roughly how many listeners?</label>
                <select id="ob-audience" value={form.audienceSize} onChange={set('audienceSize')}>
                  {AUDIENCE.map(([v, label]) => (
                    <option key={v} value={v}>{label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="ob-field">
              <label htmlFor="ob-stream">
                Do you already have a stream? <span className="opt">Optional</span>
              </label>
              <input
                id="ob-stream"
                value={form.streamUrl}
                onChange={set('streamUrl')}
                placeholder="https://..."
              />
            </div>

            <div className="ob-field">
              <label htmlFor="ob-links">
                Website and social links <span className="opt">Optional</span>
              </label>
              <input id="ob-links" value={form.links} onChange={set('links')} />
            </div>

            <div className="ob-field">
              <label>What are you interested in?</label>
              <div className="ob-checks">
                {SERVICES.map(([key, label]) => (
                  <label className="ob-check" key={key}>
                    <input
                      type="checkbox"
                      checked={services.has(key)}
                      onChange={() => toggleService(key)}
                    />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="ob-field">
              <label htmlFor="ob-note">
                Anything else we should know? <span className="opt">Optional</span>
              </label>
              <textarea
                id="ob-note"
                value={form.applicantNote}
                onChange={set('applicantNote')}
                placeholder="A launch date you are working towards, an existing contract, anything unusual about your setup."
              />
            </div>

            <div className="ob-hp" aria-hidden="true">
              <label htmlFor="website_confirm">Leave this field empty</label>
              <input
                id="website_confirm"
                name="website_confirm"
                tabIndex={-1}
                autoComplete="off"
                value={form.website_confirm}
                onChange={set('website_confirm')}
              />
            </div>

            <div className="ob-actions">
              <span className="fineprint">
                We only use this to talk to you about your station. Nothing is charged and
                nothing goes live.
              </span>
              <button className="ob-btn" type="submit" disabled={state === 'sending'}>
                {state === 'sending' ? 'Sending…' : 'Send application'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function Bar() {
  return (
    <div className="ob-bar">
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
        <circle cx="12" cy="14" r="4" />
        <path d="M4 14a8 8 0 0 1 8-8" />
        <path d="M20 8.5 8.5 13" />
      </svg>
      <strong>Creator Rabbit</strong>
    </div>
  );
}
